<script lang="ts" setup>
import { PropType } from 'vue'
import { Editor, Toolbar } from '@wangeditor-next/editor-for-vue'
import { i18nChangeLanguage, IDomEditor, IEditorConfig } from '@wangeditor-next/editor'
import { propTypes } from '@/utils/propTypes'
import { isNumber } from '@/utils/is'
import { ElMessage } from 'element-plus'
import { useLocaleStore } from '@/store/modules/locale'
import TenantMaterialSelector from '@/components/TenantMaterialSelector/index.vue'
import type { FileVO } from '@/api/infra/file'
import merge from 'lodash-es/merge'

defineOptions({ name: 'Editor' })

type InsertImageFn = (url: string, alt: string, href: string) => void
type InsertVideoFn = (url: string, poster: string) => void

const localeStore = useLocaleStore()

const currentLocale = computed(() => localeStore.getCurrentLocale)

i18nChangeLanguage(unref(currentLocale).lang)

const props = defineProps({
  editorId: propTypes.string.def('wangEditor-1'),
  height: propTypes.oneOfType([Number, String]).def('500px'),
  editorConfig: {
    type: Object as PropType<Partial<IEditorConfig>>,
    default: () => undefined
  },
  readonly: propTypes.bool.def(false),
  modelValue: propTypes.string.def(''),
  /** 素材库默认业务模块 */
  directory: propTypes.string.def('editor'),
  /**
   * 是否先开统一素材库。
   * 公众号草稿等需走微信素材上传的场景传 false，并自行传 editorConfig.uploadImage。
   */
  useMaterialLibrary: propTypes.bool.def(true)
})

const emit = defineEmits(['change', 'update:modelValue'])

const materialVisible = ref(false)
const materialType = ref<'IMAGE' | 'VIDEO'>('IMAGE')
let pendingImageInsert: InsertImageFn | null = null
let pendingVideoInsert: InsertVideoFn | null = null

const openImageMaterial = (insertFn: InsertImageFn) => {
  pendingImageInsert = insertFn
  pendingVideoInsert = null
  materialType.value = 'IMAGE'
  materialVisible.value = true
}

const openVideoMaterial = (insertFn: InsertVideoFn) => {
  pendingVideoInsert = insertFn
  pendingImageInsert = null
  materialType.value = 'VIDEO'
  materialVisible.value = true
}

const onMaterialConfirm = (items: FileVO[]) => {
  if (!items?.length) return
  if (materialType.value === 'IMAGE' && pendingImageInsert) {
    const insertFn = pendingImageInsert
    items.forEach((item) => {
      if (!item.url) return
      const alt = item.name || 'image'
      insertFn(item.url, alt, item.url)
    })
  } else if (materialType.value === 'VIDEO' && pendingVideoInsert) {
    const insertFn = pendingVideoInsert
    items.forEach((item) => {
      if (!item.url) return
      insertFn(item.url, '')
    })
  }
  pendingImageInsert = null
  pendingVideoInsert = null
}

const buildMaterialMenus = () => ({
  ['uploadImage']: {
    customBrowseAndUpload(insertFn: InsertImageFn) {
      openImageMaterial(insertFn)
    }
  },
  ['uploadVideo']: {
    customBrowseAndUpload(insertFn: InsertVideoFn) {
      openVideoMaterial(insertFn)
    }
  }
})

// 编辑器实例，必须用 shallowRef
const editorRef = shallowRef<IDomEditor>()

const valueHtml = ref('')

watch(
  () => props.modelValue,
  (val: string) => {
    if (!val) {
      val = ''
    }
    if (val === unref(valueHtml)) return
    valueHtml.value = val
  },
  {
    immediate: true
  }
)

watch(
  () => valueHtml.value,
  (val: string) => {
    emit('update:modelValue', val)
  }
)
watch(
  () => props.readonly,
  async (val) => {
    if (!editorRef.value) {
      await nextTick()
    }
    if (val) {
      editorRef.value?.disable()
    } else {
      editorRef.value?.enable()
    }
  }
)

const handleCreated = (editor: IDomEditor) => {
  editorRef.value = editor
}

// 编辑器配置：插图/插视频一律先开素材库（选用不耗；库内上传才耗）
const editorConfig = computed((): IEditorConfig => {
  return merge(
    {
      placeholder: '请输入内容...',
      readOnly: props.readonly,
      // 0 = 不限制字数（wangEditor：maxLength<=0 视为无限）
      maxLength: 0,
      customAlert: (s: string, t: string) => {
        switch (t) {
          case 'success':
            ElMessage.success(s)
            break
          case 'info':
            ElMessage.info(s)
            break
          case 'warning':
            ElMessage.warning(s)
            break
          case 'error':
            ElMessage.error(s)
            break
          default:
            ElMessage.info(s)
            break
        }
      },
      autoFocus: false,
      scroll: true,
      EXTEND_CONF: {
        mentionConfig: {
          showModal: () => {},
          hideModal: () => {}
        }
      },
      // 素材库开：接管选图；关：不注入上传菜单，交给 editorConfig（如公众号微信上传）
      MENU_CONF: props.useMaterialLibrary ? buildMaterialMenus() : {},
      uploadImgShowBase64: true
    },
    props.editorConfig || {}
  )
})

const editorStyle = computed(() => {
  return {
    height: isNumber(props.height) ? `${props.height}px` : props.height
  }
})

const handleChange = (editor: IDomEditor) => {
  emit('change', editor)
}

onBeforeUnmount(() => {
  const editor = unref(editorRef.value)
  editor?.destroy()
})

const getEditorRef = async (): Promise<IDomEditor> => {
  await nextTick()
  return unref(editorRef.value) as IDomEditor
}

defineExpose({
  getEditorRef
})
</script>

<template>
  <div class="border-1 border-solid border-[var(--tags-view-border-color)] z-10">
    <Toolbar
      :editor="editorRef"
      :editorId="editorId"
      class="border-0 b-b-1 border-solid border-[var(--tags-view-border-color)]"
    />
    <Editor
      v-model="valueHtml"
      :defaultConfig="editorConfig"
      :editorId="editorId"
      :style="editorStyle"
      @on-change="handleChange"
      @on-created="handleCreated"
    />
    <TenantMaterialSelector
      v-if="useMaterialLibrary"
      v-model="materialVisible"
      :default-module="directory"
      :file-size-mb="materialType === 'VIDEO' ? 200 : 10"
      :media-type="materialType"
      :multiple="materialType === 'IMAGE'"
      @confirm="onMaterialConfirm"
    />
  </div>
</template>

<style src="@wangeditor-next/editor/dist/css/style.css"></style>
